import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const ThemeContext = createContext(null);

const STORAGE_KEYS = {
  theme: "studio-theme-mode",
  accent: "studio-theme-accent",
  palettes: "studio-theme-palettes",
};

const DEFAULT_LIGHT_PALETTE = {
  background: "#f6f8fc",
  backgroundSoft: "#eef3fb",
  card: "#ffffff",
  subCard: "#f8fbff",
  text: "#152235",
  subText: "#6b7a90",
  border: "#dbe4f0",
  topBar: "#ffffff",
  topBarText: "#152235",
  tab: "#eef3fb",
  tabText: "#27405f",
  tabActive: "#5b7cff",
  tabActiveText: "#ffffff",
};

const DEFAULT_DARK_PALETTE = {
  background: "#0b1220",
  backgroundSoft: "#111a2d",
  card: "#121c30",
  subCard: "#17233a",
  text: "#f2f6ff",
  subText: "#98a7bc",
  border: "#26354d",
  topBar: "#10192b",
  topBarText: "#f2f6ff",
  tab: "#172338",
  tabText: "#d7e1f0",
  tabActive: "#5b7cff",
  tabActiveText: "#ffffff",
};

const getDefaultPalettes = () => ({
  light: DEFAULT_LIGHT_PALETTE,
  dark: DEFAULT_DARK_PALETTE,
});

const readLocal = (key, fallback) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    return fallback;
  }
};

const readStringLocal = (key, fallback) => {
  try {
    return localStorage.getItem(key) || fallback;
  } catch (error) {
    return fallback;
  }
};

const hexToRgb = (hex) => {
  const safe = (hex || "#5b7cff").replace("#", "");
  const normalized =
    safe.length === 3
      ? safe
          .split("")
          .map((char) => char + char)
          .join("")
      : safe;

  const bigint = parseInt(normalized, 16);

  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
};

const toRgba = (hex, alpha) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() =>
    readStringLocal(STORAGE_KEYS.theme, "light"),
  );

  const [accentColor, setAccentColor] = useState(() =>
    readStringLocal(STORAGE_KEYS.accent, "#5b7cff"),
  );

  const [palettes, setPalettes] = useState(() =>
    readLocal(STORAGE_KEYS.palettes, getDefaultPalettes()),
  );

  const safeTheme = theme === "dark" ? "dark" : "light";
  const isDark = safeTheme === "dark";

  const palette =
    palettes[safeTheme] ||
    (safeTheme === "dark" ? DEFAULT_DARK_PALETTE : DEFAULT_LIGHT_PALETTE);

  const setPaletteColor = (key, value) => {
    setPalettes((prev) => ({
      ...prev,
      [safeTheme]: {
        ...(prev[safeTheme] ||
          (safeTheme === "dark"
            ? DEFAULT_DARK_PALETTE
            : DEFAULT_LIGHT_PALETTE)),
        [key]: value,
      },
    }));
  };

  const resetPalette = () => {
    setPalettes((prev) => ({
      ...prev,
      [safeTheme]:
        safeTheme === "dark"
          ? { ...DEFAULT_DARK_PALETTE }
          : { ...DEFAULT_LIGHT_PALETTE },
    }));
  };

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.theme, safeTheme);
  }, [safeTheme]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.accent, accentColor);
  }, [accentColor]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.palettes, JSON.stringify(palettes));
  }, [palettes]);

  useEffect(() => {
    const root = document.documentElement;

    root.setAttribute("data-theme", safeTheme);

    root.style.setProperty("--app-bg", palette.background);
    root.style.setProperty("--app-bg-soft", palette.backgroundSoft);
    root.style.setProperty("--app-card", palette.card);
    root.style.setProperty("--app-sub-card", palette.subCard);
    root.style.setProperty("--app-text", palette.text);
    root.style.setProperty("--app-subtext", palette.subText);
    root.style.setProperty("--app-border", palette.border);
    root.style.setProperty("--app-top-bar", palette.topBar);
    root.style.setProperty("--app-top-bar-text", palette.topBarText);
    root.style.setProperty("--app-tab", palette.tab);
    root.style.setProperty("--app-tab-text", palette.tabText);
    root.style.setProperty("--app-tab-active", palette.tabActive);
    root.style.setProperty("--app-tab-active-text", palette.tabActiveText);

    root.style.setProperty("--app-accent", accentColor);
    root.style.setProperty(
      "--app-accent-soft",
      toRgba(accentColor, isDark ? 0.22 : 0.12),
    );
    root.style.setProperty(
      "--app-accent-soft-strong",
      toRgba(accentColor, isDark ? 0.32 : 0.18),
    );
    root.style.setProperty(
      "--app-accent-border",
      toRgba(accentColor, isDark ? 0.38 : 0.28),
    );
    root.style.setProperty(
      "--app-accent-shadow",
      toRgba(accentColor, isDark ? 0.28 : 0.2),
    );
    root.style.setProperty(
      "--app-accent-shadow-strong",
      toRgba(accentColor, isDark ? 0.42 : 0.32),
    );

    document.body.style.background = palette.background;
    document.body.style.color = palette.text;
    document.body.style.transition = "background 0.25s ease, color 0.25s ease";
  }, [palette, accentColor, safeTheme, isDark]);

  const value = useMemo(
    () => ({
      theme: safeTheme,
      isDark,
      setTheme,
      accentColor,
      setAccentColor,
      palette,
      palettes,
      setPaletteColor,
      resetPalette,
    }),
    [safeTheme, isDark, accentColor, palette, palettes],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error("useTheme must be used inside ThemeProvider");
  }

  return context;
}
